import { useState, useEffect, useRef } from 'react';
import styles from './bulk-add-to-collection-picker.module.css';
import { Flex, Input, useReducedMotion } from '@nula/core-ui';
import { motion, AnimatePresence } from 'motion/react';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';

const MAX_RESULTS = 5;

interface BulkAddToCollectionPickerProps {
  open: boolean;
  onClose: () => void;
  onAdd: (collectionId: string) => void;
}

export function BulkAddToCollectionPicker({ open, onClose, onAdd }: BulkAddToCollectionPickerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const shouldReduce = useReducedMotion();
  const [search, setSearch] = useState('');
  const collections = useWorkspaceCollections();

  const results = collections
    // Never offer archived collections — you can't add documents to one.
    .filter((c) => !c.isArchived)
    .filter((c) => c.name.toLowerCase().includes(search.toLowerCase()))
    .slice(0, MAX_RESULTS);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          initial={shouldReduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
          animate={shouldReduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={shouldReduce ? { opacity: 0 } : { opacity: 0, y: 4 }}
          transition={{ duration: 0.15 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={styles.panel}
        >
          <Flex vertical className={styles.content}>
            <Input
              autoFocus
              size="sm"
              variant="plain"
              aria-label="Search collections"
              placeholder="Search collections…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
            {results.length > 0 && (
              <Flex vertical className={styles.results}>
                {results.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={styles.option}
                    onClick={() => { onAdd(c.id); onClose(); }}
                  >
                    {c.name}
                  </button>
                ))}
              </Flex>
            )}
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
